import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { LoadingButton } from '@mui/lab';
import { Container, IconButton, InputAdornment, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { LocalStorage } from 'src/utils/storage/local-storage';
import { useToaster } from 'src/utils/toaster/toasterContext';
import ValidationTool from 'src/utils/validationHelper';
import landingImage from '../../assets/app-image/landing.png';
import useResponsive from '../../hooks/useResponsive';

const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledSection = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 480,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.default,
}));

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
}));

const AffiliateRegistration = () => {
  const { t: translate } = useTranslation();
  const mdUp = useResponsive('up', 'md');
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formSchema, setFormSchema] = useState({});
  const [formValidationErrors, setFormValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const { showToast } = useToaster();
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [timeZone, setTimeZone] = useState('UTC');
  const appLogo = CommonHelper.GetAppLogo();

  useEffect(() => {
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (localTimeZone) setTimeZone(localTimeZone);
    let token = LocalStorage.getItem('token');
    if (token) navigate('/new-post');
    else generateFormModelSchema();
  }, []);

  useEffect(() => {}, [email, password, name, formSchema, formValidationErrors]);

  const generateFormModelSchema = () => {
    let fields = [
      ValidationTool.GenerateSchema('name', translate('profile.general.first_name'), 'TEXT', true),
      ValidationTool.GenerateSchema('email', translate('profile.general.email'), 'EMAIL', true),
      ValidationTool.GenerateSchema('password', translate('profile.general.first_name'), 'TEXT', true),
    ];
    const schema = ValidationTool.GenerateSchemaModel(fields, translate);
    setFormSchema(schema);
  };

  const handleInputChange = (event) => {
    let value = event.target.value;
    let name = event.target.name;
    switch (name) {
      case 'email':
        setEmail(value);
        break;
      case 'password':
        setPassword(value);
        break;
      case 'name':
        setName(value);
        break;
      default:
        break;
    }
  };

  const handleOnBlur = (event) => {
    let fieldName = event.target.name;
    let fieldValue = event.target.value;
    let validationErrors = { ...formValidationErrors };
    let message = ValidationTool.ValidateInput(formSchema, fieldValue, fieldName);
    if (message) validationErrors[fieldName] = message;
    else validationErrors[fieldName] = '';
    setFormValidationErrors(validationErrors);
  };

  const handleRegister = () => {
    let registerForm = {
      name: name,
      email: email,
      password: password,
      device_type: isMobile ? CommonHelper.DeviceType.Mobile : CommonHelper.DeviceType.Desktop,
    };
    let { formValidationErrors, isValidForm } = ValidationTool.FormValidation(formSchema, registerForm);
    if (isValidForm) handleCreateNewUser();
    else setFormValidationErrors(formValidationErrors);
  };
  const handleCreateNewUser = () => {
    setLoading(true);
    let userInfo = {
      first_name: name,
      email: email,
      password: password,
      timezone: timeZone,
    };
    let registerForm = {
      user_info: userInfo,
    };
    let postProps = {
      url: userModuleURL.affiliateRegisteration,
      body: registerForm,
      successCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };
  const successCallback = (data, message) => {
    data['is_register'] = true;
    message && showToast(message);
    CommonHelper.AppLoginCall(data);
  };
  const failureCallback = (message) => {
    message && showToast(message, 'e');
    setLoading(false);
  };
  return (
    <>
      <Helmet>
        <title>{translate('common.register')}</title>
      </Helmet>

      <StyledRoot>
        {mdUp && (
          <>
            <img
              src={appLogo}
              style={{
                height: '40px',
                position: 'fixed',
                top: '34px',
                left: '35px',
              }}
            />
            <StyledSection>
              <Typography variant="h3" sx={{ px: 5, mt: 10, mb: 5 }}>
                {translate('affiliate.welcome_to')} {CommonHelper.GetAppName()}
              </Typography>
              <img src={landingImage} alt="Register" />
            </StyledSection>
          </>
        )}
        <Container maxWidth="sm">
          <StyledContent>
            <Typography variant="h4" sx={{ mb: 3 }} gutterBottom>
              {translate('common.register_in')} {CommonHelper.GetAppName()}
            </Typography>

            <Stack spacing={3} sx={{ mb: 5 }}>
              <TextField
                error={formValidationErrors?.name}
                color={name === '' ? 'error' : 'success'}
                name="name"
                label={translate('common.name')}
                onChange={handleInputChange}
                value={name}
                helperText={formValidationErrors?.name}
                onBlur={handleOnBlur}
              />
              <TextField
                error={formValidationErrors?.email}
                color={email === '' ? 'error' : 'success'}
                name="email"
                label={translate('profile.general.email')}
                onChange={handleInputChange}
                value={email}
                helperText={formValidationErrors?.email}
                onBlur={handleOnBlur}
              />
              <TextField
                error={formValidationErrors?.password}
                color={password === '' ? 'error' : 'success'}
                name="password"
                label={translate('profile.general.password')}
                type={showPassword ? 'text' : 'password'}
                onChange={handleInputChange}
                onBlur={handleOnBlur}
                value={password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={formValidationErrors?.password}
              />
            </Stack>
            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              loading={loading}
              variant="contained"
              onClick={handleRegister}
            >
              {translate('common.register')}
            </LoadingButton>
          </StyledContent>
        </Container>
      </StyledRoot>
    </>
  );
};
export default AffiliateRegistration;
